'use client';

import { FC, useState, useContext } from 'react';
import { Flex, Toggle } from '@/_components';
import { SettingsFormProps } from './SettingsForm.types';
import { updateCompany } from '@/_api/admin-companies';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { Company } from '@/_types/models/company';
import { EntityV2Context } from '@/_contexts/admin/EntityV2Provider';

export const SettingsForm: FC<SettingsFormProps> = ({ company }) => {
  const { token } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);
  const { updateSelectedCompany } = useContext(CompanyContext);
  const { loadEntities } = useContext(EntityV2Context);

  const [isLoading, setLoading] = useState(false);
  const [selectedLocalCompany, setSelectedLocalCompany] = useState(company);

  const refresh = (response: Company) => {
    updateSelectedCompany(response);
    setSelectedLocalCompany(response);
    loadEntities();
    addToast('success', 'Entity updated successfully');
  };

  const onToggleDeactivateEntity = async (
    _event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    try {
      setLoading(true);

      const response = await updateCompany({
        token,
        companyId: selectedLocalCompany?.id!,
        inactive: !selectedLocalCompany.inactive,
        userId: selectedLocalCompany?.user?.id!,
      });

      if (!response.error) {
        refresh(response);
      }
    } catch (error) {
      return addToast('error', 'error updating entity', { error });
    } finally {
      setLoading(false);
    }
  };

  const onToggleReadOnlyEntity = async () => {
    try {
      setLoading(true);

      const response = await updateCompany({
        token,
        companyId: selectedLocalCompany?.id!,
        readonly_access: !selectedLocalCompany?.readonly_access,
        userId: selectedLocalCompany?.user?.id!,
      });

      if (!response.error) {
        refresh(response);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Flex isColumn={true} gap={16}>
        <Flex isColumn={true} gap={6}>
          <Toggle
            label="Deactivate Entity"
            isChecked={!!selectedLocalCompany?.inactive}
            isDisabled={isLoading}
            onChange={onToggleDeactivateEntity}
            helperText="Its users will not be able to access this entity"
          />
          <Toggle
            label="Read-only Entity"
            isChecked={!!selectedLocalCompany?.readonly_access}
            isDisabled={isLoading || !!selectedLocalCompany?.inactive}
            onChange={onToggleReadOnlyEntity}
            helperText="Its users can still access existing documents, but they will not be able to upload new documents or complete questionnaires for this entity"
          />
        </Flex>
      </Flex>
    </>
  );
};
